import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import { Building2, Users, Phone, Mail, TrendingUp, Calendar, MapPin, Upload, Linkedin, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentInteractions, setRecentInteractions] = useState([]);
  const [regionStats, setRegionStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [companiesRes, interactionsRes, palmCoastRes, orlandoRes] = await Promise.all([
          api.get('/api/companies?limit=5'),
          api.get('/api/interactions/stats/overview'),
          api.get('/api/companies?region=Palm%20Coast&limit=100'),
          api.get('/api/companies?region=Orlando&limit=100')
        ]);

        setRecentCompanies(companiesRes.data.companies || []);
        setStats(interactionsRes.data.stats);
        setRecentInteractions(interactionsRes.data.recentActivity);
        
        // Calculate region statistics
        setRegionStats({
          'Palm Coast': palmCoastRes.data.companies?.length || 0,
          'Orlando': orlandoRes.data.companies?.length || 0
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'email':
        return '📧';
      case 'phone':
        return '📞';
      case 'meeting':
        return '🤝';
      default:
        return '📝';
    }
  };

  const getInteractionColor = (type) => {
    switch (type) {
      case 'email':
        return 'text-blue-600';
      case 'phone':
        return 'text-green-600';
      case 'meeting':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  const openLinkedIn = (url) => {
    if (!url) return;
    
    // Ensure URL has protocol
    let formattedUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      formattedUrl = 'https://' + url;
    }
    
    window.open(formattedUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of your contact management system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Companies</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {recentCompanies.length > 0 ? '...' : '0'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Contacts</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats?.contacts_contacted || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Phone className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Interactions</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats?.total_interactions || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Follow-ups Pending</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats?.follow_ups_pending || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Region Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Region Statistics</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="ml-3 font-medium text-gray-900">Palm Coast</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">{regionStats['Palm Coast'] || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="ml-3 font-medium text-gray-900">Orlando</span>
                </div>
                <span className="text-2xl font-bold text-green-600">{regionStats['Orlando'] || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Companies */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Companies</h3>
              <Link to="/companies" className="text-sm text-blue-600 hover:text-blue-800">
                View all
              </Link>
            </div>
          </div>
          <div className="card-body">
            {recentCompanies.length > 0 ? (
              <div className="space-y-4">
                {recentCompanies.map((company) => (
                  <div key={company.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{company.name}</p>
                        <div className="flex items-center text-xs text-gray-500">
                          <MapPin className="h-3 w-3 mr-1" />
                          {company.country_code}
                          {company.type && (
                            <>
                              <span className="mx-2">•</span>
                              <span className="capitalize">{company.type}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/companies/${company.id}`}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      View
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building2 className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                <p className="text-gray-500">No companies yet</p>
                <Link to="/import" className="text-blue-600 hover:text-blue-800 text-sm">
                  Import your first company
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              <Link to="/interactions" className="text-sm text-blue-600 hover:text-blue-800">
                View all
              </Link>
            </div>
          </div>
          <div className="card-body">
            {recentInteractions && recentInteractions.length > 0 ? (
              <div className="space-y-4">
                {recentInteractions.map((interaction, index) => (
                  <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <span className="text-2xl">{getInteractionIcon(interaction.type)}</span>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)} with{' '}
                        {interaction.company_name}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(interaction.created_at).toLocaleDateString()}
                        <span className="mx-2">•</span>
                        <span className={getInteractionColor(interaction.type)}>
                          {interaction.method}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Phone className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No recent interactions</p>
                <Link to="/interactions" className="text-blue-600 hover:text-blue-800 text-sm">
                  Start tracking interactions
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/import"
              className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Upload className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="font-medium text-blue-900">Import Data</p>
                <p className="text-sm text-blue-700">Add companies and contacts</p>
              </div>
            </Link>

            <Link
              to="/interactions/new"
              className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <Phone className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-green-900">Log Interaction</p>
                <p className="text-sm text-green-700">Record a call or email</p>
              </div>
            </Link>

            <Link
              to="/companies"
              className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <Building2 className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <p className="font-medium text-purple-900">Add Company</p>
                <p className="text-sm text-purple-700">Create new company</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
